import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usersApi } from '@/lib/api/users';
import { useAuth } from '@/lib/auth';
import { RoleOptions, type Role, type UpdateUserRequest, type UserResponse } from '@paladin-monorepo/common';

interface EditUserDialogProps {
  user: UserResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditUserDialog({ user, open, onOpenChange }: EditUserDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth({ middleware: 'auth', redirectTo: '/auth/login', tmp: 'EditUserDialog' });
  const [selectedRole, setSelectedRole] = useState<Role>(user.role);

  // Check if user is editing themselves (cannot change own role)
  const isEditingSelf = currentUser?.id === user.id;

  const schema = yup.object({
    email: yup
      .string()
      .email(t('users.edit.form.email.errors.invalid'))
      .required(t('users.edit.form.email.errors.required')),
    firstName: yup
      .string()
      .required(t('users.edit.form.firstName.errors.required')),
    lastName: yup
      .string()
      .required(t('users.edit.form.lastName.errors.required')),
  }).required();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<UpdateUserRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: user.email,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateUserRequest) => usersApi.update(user.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(t('users.edit.success'));
      onOpenChange(false);
    },
    onError: (error: any) => {
      if (error.response?.data?.code === 'EmailAlreadyExists') {
        setError('email', { message: t('users.edit.form.email.errors.exists') });
      } else if (error.response?.data?.code === 'CannotDemoteSelf') {
        toast.error(t('users.edit.errors.cannotDemoteSelf'));
      } else {
        toast.error(t('users.edit.error'));
      }
    },
  });

  const onSubmit = (data: UpdateUserRequest) => {
    updateMutation.mutate({
      ...data,
      role: selectedRole,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{t('users.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('users.edit.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('users.edit.form.email.label')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('users.edit.form.email.placeholder')}
                {...register('email')}
                disabled={updateMutation.isPending}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('users.edit.form.firstName.label')}</Label>
                <Input
                  id="firstName"
                  placeholder={t('users.edit.form.firstName.placeholder')}
                  {...register('firstName')}
                  disabled={updateMutation.isPending}
                />
                {errors.firstName && (
                  <p className="text-sm text-destructive">{errors.firstName.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t('users.edit.form.lastName.label')}</Label>
                <Input
                  id="lastName"
                  placeholder={t('users.edit.form.lastName.placeholder')}
                  {...register('lastName')}
                  disabled={updateMutation.isPending}
                />
                {errors.lastName && (
                  <p className="text-sm text-destructive">{errors.lastName.message}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('users.edit.form.role.label')}</Label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as Role)}
                disabled={updateMutation.isPending || isEditingSelf}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('users.edit.form.role.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={RoleOptions.ACCOUNT_ADMIN}>
                    {t('codebook.role.ACCOUNT_ADMIN')}
                  </SelectItem>
                  <SelectItem value={RoleOptions.EMPLOYEE}>
                    {t('codebook.role.EMPLOYEE')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {isEditingSelf
                  ? t('users.edit.form.role.hintSelf')
                  : t('users.edit.form.role.hint')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              {t('generic.cancel')}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t('generic.loading') : t('users.edit.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
